import React, { forwardRef } from "react";
import { formatDateTime } from "../../utils/dateTime";
import moment from "moment";
import {
  THERMAL_WIDTH_MM,
  THERMAL_PADDING_H_MM,
  THERMAL_PADDING_V_MM,
} from "../Thermal/thermalConfig";

export { THERMAL_WIDTH_MM } from "../Thermal/thermalConfig";

/** Default shop block — matches thermal slip branding */
export const THERMAL_SHOP_DEFAULTS = {
  shopTitleLine: "Irshad Carton Dealer, Charbagh Swat",
  logoSrc: "/logo2.svg",
  managerLabel: "Manager",
  managerPhone: "03402742727",
  ownerLabel: "Wajahat",
  ownerPhone: "03008155174",
  /** @deprecated use shopTitleLine */
  shopName: "IRSHAD CARTON DEALER",
  address: "Madyan Road, Charbagh, Swat",
  ownerContact: "Wajahat Ali - 03008155174",
  managerContact: "Abdullah - 03402742727",
};

const parseNamePhone = (value) => {
  const s = String(value ?? "").trim();
  const match = s.match(/^(.+?)\s*[-–]\s*(.+)$/);
  if (match) return { name: match[1].trim(), phone: match[2].trim() };
  return { name: s, phone: "" };
};

const ShopStaffRow = ({ leftLabel, leftPhone, rightLabel, rightPhone }) => (
  <div className="tr-staff-row">
    <div className="tr-staff-col">
      <div className="tr-staff-label">{leftLabel}</div>
      <div className="tr-staff-phone">{leftPhone}</div>
    </div>
    <div className="tr-staff-col tr-staff-col-right">
      <div className="tr-staff-label">{rightLabel}</div>
      <div className="tr-staff-phone">{rightPhone}</div>
    </div>
  </div>
);

const formatRs = (n) =>
  `Rs ${Number(n || 0).toLocaleString("en-PK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatNum = (n) =>
  Number(n || 0).toLocaleString("en-PK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const DashedRule = () => <div className="tr-dash" />;
const SolidRule = ({ thick }) => <div className={thick ? "tr-solid-thick" : "tr-solid"} />;

const SectionTitle = ({ children, className = "" }) => (
  <div className={`tr-section-title ${className}`.trim()}>{children}</div>
);

/** Single customer line: name · phone · address — label + icon + full-width value */
const CustomerInfoRow = ({ customer }) => {
  const parts = [customer?.name, customer?.contact, customer?.address].filter(
    (p) => p && String(p).trim() !== ""
  );
  const summary = parts.length ? parts.join(" - ") : "—";

  return (
    <div className="tr-customer-row">
      <div className="tr-customer-label">
        <span className="tr-icon">👤</span>
        <span>Customer:</span>
      </div>
      <div className="tr-customer-value">{summary}</div>
    </div>
  );
};

const PaymentRow = ({ label, value, negative }) => (
  <div className="tr-pay-row">
    <span>{label}</span>
    <span className="tr-pay-value">
      {negative ? `- ${value}` : value}
    </span>
  </div>
);

const ThermalCustomerInvoice = forwardRef(({ data }, ref) => {
  if (!data) return null;

  const {
    shopTitleLine,
    shopName = THERMAL_SHOP_DEFAULTS.shopName,
    address = THERMAL_SHOP_DEFAULTS.address,
    managerLabel = THERMAL_SHOP_DEFAULTS.managerLabel,
    managerPhone: managerPhoneProp,
    managerContact = THERMAL_SHOP_DEFAULTS.managerContact,
    ownerLabel = THERMAL_SHOP_DEFAULTS.ownerLabel,
    ownerPhone: ownerPhoneProp,
    ownerContact = THERMAL_SHOP_DEFAULTS.ownerContact,
    logoSrc = THERMAL_SHOP_DEFAULTS.logoSrc,
    customer,
    invoice_no,
    auto_invoice_no,
    date,
    items = [],
    discount = 0,
    payment = 0,
    billTotal,
    loadingExpense = 0,
    saleType = "SALE",
    invoiceCustomerType = "CUSTOMER",
    printMode,
    showPrices = true,
    desc,
  } = data;

  const resolvedPrintMode =
    printMode ?? (showPrices === false ? "qtyOnly" : "full");
  const showItemRate = resolvedPrintMode === "full";
  const showItemAmount = resolvedPrintMode !== "qtyOnly";
  const showPayment = resolvedPrintMode !== "qtyOnly";
  const tableColCount = 2 + (showItemRate ? 1 : 0) + (showItemAmount ? 1 : 0);
  const productColWidth =
    tableColCount === 4 ? "44%" : tableColCount === 3 ? "54%" : "72%";

  const itemsSum =
    billTotal ??
    items.reduce((s, i) => s + Number(i.amount || i.qty * i.price || 0), 0);

  const totalQty = items.reduce((s, i) => s + Number(i.qty || 0), 0);
  const netBill = Number(itemsSum) - Number(discount || 0);
  const cashReceived = Number(payment || 0);
  const loading = Number(loadingExpense || 0);
  const netAmount = Math.max(0, netBill - cashReceived - loading);

  const formattedDate = formatDateTime(date || Math.floor(Date.now() / 1000));

  const isReturn = saleType === "RETURN";
  const isGuest =
    invoiceCustomerType === "GUEST" || invoiceCustomerType === "WALK_IN";

  const titleLine =
    shopTitleLine ||
    THERMAL_SHOP_DEFAULTS.shopTitleLine ||
    [shopName, address?.includes("Charbagh") ? "Charbagh Swat" : address]
      .filter(Boolean)
      .join(", ");

  const managerParsed = parseNamePhone(managerContact);
  const ownerParsed = parseNamePhone(ownerContact);
  const leftStaffLabel = managerLabel || "Manager";
  const leftStaffPhone =
    managerPhoneProp || managerParsed.phone || THERMAL_SHOP_DEFAULTS.managerPhone;
  const rightStaffLabel =
    ownerLabel || ownerParsed.name.split(" ")[0] || "Wajahat";
  const rightStaffPhone =
    ownerPhoneProp || ownerParsed.phone || THERMAL_SHOP_DEFAULTS.ownerPhone;

  return (
    <div ref={ref} className="thermal-receipt">
      <style>{`
        @media print {
          @page { size: ${THERMAL_WIDTH_MM}mm auto; margin: 0; }
          html, body { margin: 0 !important; padding: 0 !important; }
          .thermal-receipt {
            width: ${THERMAL_WIDTH_MM}mm !important;
            max-width: ${THERMAL_WIDTH_MM}mm !important;
            padding: ${THERMAL_PADDING_V_MM}mm ${THERMAL_PADDING_H_MM}mm !important;
          }
        }
        .thermal-receipt {
          width: ${THERMAL_WIDTH_MM}mm;
          max-width: ${THERMAL_WIDTH_MM}mm;
          min-width: ${THERMAL_WIDTH_MM}mm;
          padding: ${THERMAL_PADDING_V_MM}mm ${THERMAL_PADDING_H_MM}mm;
          margin: 0 auto;
          background: #fff;
          color: #000;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 12px;
          line-height: 1.45;
          box-sizing: border-box;
        }
        .thermal-receipt * { box-sizing: border-box; }
        .tr-header-meta {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 4px;
          margin-bottom: 3px;
        }
        .tr-date-top {
          font-size: 11px;
          font-weight: 700;
          line-height: 1.25;
          flex-shrink: 0;
        }
        .tr-header-right {
          flex-shrink: 0;
          text-align: right;
          font-size: 10px;
          line-height: 1.35;
          max-width: 50%;
        }
        .tr-header-right-line {
          font-weight: 700;
          word-break: break-word;
        }
        .tr-header-right-line span {
          font-weight: 600;
        }
        .tr-logo-wrap {
          text-align: center;
          margin-bottom: 3px;
        }
        .tr-logo {
          width: 112px;
          height: 112px;
          object-fit: contain;
          margin: 0 auto;
          display: block;
        }
        .tr-shop-title {
          text-align: center;
          font-size: 12px;
          font-weight: 700;
          line-height: 1.3;
          margin-bottom: 5px;
          letter-spacing: 0.15px;
        }
        .tr-staff-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 8px;
          margin-bottom: 4px;
        }
        .tr-staff-col {
          flex: 1;
          min-width: 0;
        }
        .tr-staff-col-right {
          text-align: right;
        }
        .tr-staff-label {
          font-size: 11px;
          font-weight: 700;
          line-height: 1.25;
          margin-bottom: 1px;
        }
        .tr-staff-phone {
          font-size: 10px;
          font-weight: 600;
          line-height: 1.3;
          word-break: break-word;
        }
        .tr-dash {
          border: none;
          border-top: 1px dashed #000;
          margin: 5px 0;
          height: 0;
        }
        .tr-solid {
          border: none;
          border-top: 1px solid #000;
          margin: 4px 0;
          height: 0;
        }
        .tr-solid-thick {
          border: none;
          border-top: 2px solid #000;
          margin: 5px 0;
          height: 0;
        }
        .tr-section-title {
          font-size: 12px;
          font-weight: 700;
          margin: 3px 0 4px;
          letter-spacing: 0.3px;
        }
        .tr-section-title-compact {
          margin-bottom: 2px;
        }
        .tr-icon { font-size: 12px; line-height: 1; }
        .tr-desc-block {
          font-size: 11px;
          font-weight: 600;
          line-height: 1.35;
          margin: 4px 0 2px;
          word-break: break-word;
        }
        .tr-desc-block span {
          font-weight: 700;
        }
        .tr-customer-row {
          padding: 3px 0;
          border-bottom: 1px solid #000;
          font-size: 11px;
        }
        .tr-customer-label {
          display: flex;
          align-items: center;
          gap: 3px;
          font-weight: 600;
          margin-bottom: 2px;
        }
        .tr-customer-value {
          font-weight: 700;
          line-height: 1.35;
          word-break: break-word;
          padding-left: 13px;
        }
        .tr-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
          margin: 2px 0;
          table-layout: fixed;
        }
        .tr-table th,
        .tr-table td {
          border: 1px solid #000;
          padding: 4px 3px;
          vertical-align: middle;
        }
        .tr-table th {
          font-weight: 700;
          text-align: center;
          background: #fff;
        }
        .tr-table td {
          text-align: center;
        }
        .tr-table td.tr-product {
          text-align: left;
          font-weight: 600;
          word-break: break-word;
        }
        .tr-table .tr-num {
          font-weight: 700;
          text-align: right;
        }
        .tr-table tfoot td {
          font-weight: 700;
          background: #f5f5f5;
        }
        .tr-table tfoot .tr-product {
          text-align: left;
        }
        .tr-pay-row {
          display: flex;
          justify-content: space-between;
          padding: 3px 0;
          border-bottom: 1px solid #000;
          font-size: 11px;
        }
        .tr-pay-row:last-of-type { border-bottom: none; }
        .tr-pay-value { font-weight: 700; }
        .tr-thanks {
          text-align: center;
          font-weight: 700;
          font-size: 12px;
          margin-top: 6px;
          letter-spacing: 0.2px;
        }
        .tr-qty-only-tag {
          text-align: center;
          font-size: 11px;
          font-weight: 700;
          border: 1px dashed #000;
          padding: 2px;
          margin-bottom: 4px;
        }
      `}</style>

      {/* ── Header: date (left), invoice #s (right), logo centered ── */}
      <div className="tr-header-meta">
        <div className="tr-date-top">{formattedDate}</div>
        <div className="tr-header-right">
          <div className="tr-header-right-line">
            <span>Inv #:</span>{" "}
            {invoice_no != null && String(invoice_no).trim() !== ""
              ? String(invoice_no).trim()
              : "—"}
          </div>
          <div className="tr-header-right-line">
            <span>Auto #:</span>{" "}
            {auto_invoice_no != null && auto_invoice_no !== ""
              ? String(auto_invoice_no)
              : "—"}
          </div>
        </div>
      </div>
      <div className="tr-logo-wrap">
        <img src={logoSrc} alt="" className="tr-logo" />
      </div>

      <div className="tr-shop-title">{titleLine}</div>

      <ShopStaffRow
        leftLabel={leftStaffLabel}
        leftPhone={leftStaffPhone}
        rightLabel={rightStaffLabel}
        rightPhone={rightStaffPhone}
      />

      <DashedRule />

      {/* ── Customer ── */}
      {/* <SectionTitle>▶ CUSTOMER</SectionTitle> */}
      <CustomerInfoRow customer={customer} />
      {isGuest && !isReturn && (
        <div className="tr-qty-only-tag">GUEST — CASH SALE</div>
      )}

      <SolidRule thick />

      {resolvedPrintMode === "qtyOnly" && (
        <div className="tr-qty-only-tag">QTY ONLY — NO PRICES</div>
      )}

      {/* ── Items table ── */}
      <table className="tr-table">
        <thead>
          <tr>
            <th style={{ width: productColWidth }}>Product</th>
            <th>Qty</th>
            {showItemRate && <th>Price</th>}
            {showItemAmount && <th>Amt</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => {
            const amt = Number(item.amount ?? item.qty * item.price ?? 0);
            return (
              <tr key={i}>
                <td className="tr-product">{item.name}</td>
                <td className="tr-num">{formatNum(item.qty)}</td>
                {showItemRate && (
                  <td className="tr-num">{formatNum(item.price)}</td>
                )}
                {showItemAmount && (
                  <td className="tr-num">{formatNum(amt)}</td>
                )}
              </tr>
            );
          })}
          {items.length === 0 && (
            <tr>
              <td colSpan={tableColCount} style={{ textAlign: "center" }}>
                No items
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <td className="tr-product">Total</td>
            <td className="tr-num">{formatNum(totalQty)}</td>
            {showItemRate && <td />}
            {showItemAmount && (
              <td className="tr-num">{formatNum(itemsSum)}</td>
            )}
          </tr>
        </tfoot>
      </table>

      {/* ── Payment ── */}
      {showPayment && (
        <>
          <SectionTitle>▶ PAYMENT</SectionTitle>
          <PaymentRow label="Bill Total:" value={formatRs(itemsSum)} />
          <PaymentRow label="Cash Received:" value={formatRs(cashReceived)} />
          <PaymentRow label="Discount:" value={formatRs(discount)} negative />
          <PaymentRow label="Net Amount:" value={formatRs(netAmount)} />

          <DashedRule />
        </>
      )}

      {desc && (
        <div className="tr-desc-block">
          <span>Description:</span> {desc}
        </div>
      )}

      <DashedRule />

      <div className="tr-thanks">★ Thank You For Your Business ★</div>
    </div>
  );
});

ThermalCustomerInvoice.displayName = "ThermalCustomerInvoice";

export default ThermalCustomerInvoice;
